import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  TextField,
  Stack,
  Chip,
  IconButton,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

interface TransferListItem {
  id: string;
  name: string;
}

interface TransferListProps {
  availableItems: TransferListItem[];
  assignedItems: TransferListItem[];
  searchText: string;
  onSearchChange: (text: string) => void;
  onMoveToAssigned: (itemId: string) => void;
  onMoveToAvailable: (itemId: string) => void;
  loading?: boolean;
}

const TransferList: React.FC<TransferListProps> = ({
  availableItems,
  assignedItems,
  searchText,
  onSearchChange,
  onMoveToAssigned,
  onMoveToAvailable,
  loading = false,
}) => {
  // Filter available items based on search text
  const filteredAvailableItems = availableItems.filter(item =>
    item.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const clearSearch = () => {
    onSearchChange('');
  };

  return (
    <Stack spacing={1.5}>
      {/* Available Items Panel */}
      <Box>
        <Stack spacing={1}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle1" component="h4" fontWeight={500}>
              Available Items ({filteredAvailableItems.length})
            </Typography>
          </Box>

          <TextField
            size="small"
            placeholder="Search available items..."
            value={searchText}
            onChange={e => onSearchChange(e.target.value)}
            disabled={loading}
            data-testid="transfer-list-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={clearSearch}
                    disabled={loading}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              height: '200px',
              overflow: 'auto',
            }}
          >
            <List dense data-testid="available-items-list">
              {filteredAvailableItems.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={
                      searchText
                        ? 'No items match your search'
                        : 'No available items'
                    }
                    sx={{ textAlign: 'center', fontStyle: 'italic' }}
                  />
                </ListItem>
              ) : (
                filteredAvailableItems.map(item => (
                  <ListItem
                    key={item.id}
                    disablePadding
                    data-testid="available-item"
                  >
                    <ListItemButton
                      onClick={() => onMoveToAssigned(item.id)}
                      disabled={loading}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemText primary={item.name} />
                      <IconButton
                        size="small"
                        disabled={loading}
                        sx={{ color: 'primary.main' }}
                        data-testid="add-item-button"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Box>

          {searchText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`Filtered by: "${searchText}"`}
                size="small"
                onDelete={clearSearch}
                disabled={loading}
              />
            </Box>
          )}
        </Stack>
      </Box>

      <Divider />

      {/* Assigned Items Panel */}
      <Box>
        <Stack spacing={1}>
          <Typography variant="subtitle1" component="h4" fontWeight={500}>
            Assigned Items ({assignedItems.length})
          </Typography>

          <Box
            sx={{
              height: '200px',
              overflow: 'auto',
            }}
          >
            <List dense data-testid="assigned-items-list">
              {assignedItems.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No items assigned to this group"
                    sx={{ textAlign: 'center', fontStyle: 'italic' }}
                  />
                </ListItem>
              ) : (
                assignedItems.map(item => (
                  <ListItem
                    key={item.id}
                    disablePadding
                    data-testid="assigned-item"
                  >
                    <ListItemButton
                      onClick={() => onMoveToAvailable(item.id)}
                      disabled={loading}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemText primary={item.name} />
                      <IconButton
                        size="small"
                        disabled={loading}
                        sx={{ color: 'error.main' }}
                        data-testid="remove-item-button"
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};

export default TransferList;
